import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCwIcon,
  Trash2Icon,
  PlusIcon,
  PlayIcon,
  SquareIcon,
} from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import {
  AdminInfraService,
  PaginatedInstances,
} from "../../services/AdminInfraService";
import { VastInstance, VastInfraService } from "../../services/VastInfraService";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { MoreHorizontalIcon } from "lucide-react";
import CreateInstanceWizard from "./CreateInstanceWizard";

const PAGE_SIZE = 10;

const STATUS_VARIANT: Record<string, string> = {
  RUNNING:
    "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  PROVISIONING:
    "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  PENDING:
    "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  STOPPED: "bg-muted text-muted-foreground border-border",
  DESTROYED: "bg-muted text-muted-foreground border-border",
  ERROR: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
};

interface ConfirmState {
  open: boolean;
  title: string;
  description: string;
  destructive?: boolean;
  onConfirm: () => void;
}

const VastInstancesOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<VastInstance[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ running: 0, provisioning: 0, total_adapters: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());

  const fetchInstances = useCallback(
    (page: number = currentPage) => {
      const offset = (page - 1) * PAGE_SIZE;
      AdminInfraService.getInstancesPaginated(offset, PAGE_SIZE, setIsLoading, apiKey).subscribe(
        (data: PaginatedInstances) => {
          setInstances(data.items);
          setTotalCount(data.total);
          setStats(data.stats);
        },
        (error: any) => {
          console.error("Failed to load instances:", error);
          toast.error("Failed to load instances");
        }
      );
    },
    [apiKey, currentPage]
  );

  useEffect(() => {
    fetchInstances(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const interval = setInterval(() => fetchInstances(currentPage), 60000);
    return () => clearInterval(interval);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const markBusy = (id: number) => setBusyIds((s) => new Set(s).add(id));
  const clearBusy = (id: number) =>
    setBusyIds((s) => { const n = new Set(s); n.delete(id); return n; });

  const handleStartInstance = (inst: VastInstance, e: React.MouseEvent) => {
    e.stopPropagation();
    markBusy(inst.id);
    AdminInfraService.startInstance(inst.id, null, apiKey).subscribe(
      () => { toast.success("Start requested"); clearBusy(inst.id); fetchInstances(currentPage); },
      () => { toast.error("Start failed"); clearBusy(inst.id); }
    );
  };

  const handleStopInstance = (inst: VastInstance, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirm({
      open: true,
      title: "Stop instance?",
      description: `Stop "${inst.base_model}" on ${inst.gpu_count}x ${inst.gpu_type}? Running workloads will be interrupted.`,
      onConfirm: () => {
        markBusy(inst.id);
        AdminInfraService.stopInstance(inst.id, null, apiKey).subscribe(
          () => { toast.success("Stop requested"); clearBusy(inst.id); fetchInstances(currentPage); },
          () => { toast.error("Stop failed"); clearBusy(inst.id); }
        );
      },
    });
  };

  const handleDestroyInstance = (inst: VastInstance, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirm({
      open: true,
      title: "Destroy instance?",
      description: `Permanently destroy "${inst.base_model}"? This cannot be undone.`,
      destructive: true,
      onConfirm: () => {
        markBusy(inst.id);
        VastInfraService.deleteInstance(inst.id, false, null, apiKey).subscribe(
          (result: { status: string }) => {
            clearBusy(inst.id);
            if (result.status === "not_found_on_vast") {
              setConfirm({
                open: true,
                title: "Instance Not Found",
                description: "Instance no longer exists on Vast. Clean up the stale entry?",
                destructive: true,
                onConfirm: () => {
                  markBusy(inst.id);
                  VastInfraService.deleteInstance(inst.id, true, null, apiKey).subscribe(
                    () => { toast.success("Stale entry removed"); clearBusy(inst.id); fetchInstances(currentPage); },
                    () => { toast.error("Failed to clean up entry"); clearBusy(inst.id); }
                  );
                },
              });
            } else {
              toast.success("Instance destroyed");
              fetchInstances(currentPage);
            }
          },
          () => { toast.error("Failed to destroy instance"); clearBusy(inst.id); }
        );
      },
    });
  };

  const formatDuration = (inst: VastInstance): string => {
    if (inst.status !== "RUNNING" || !inst.date_updated) return "—";
    const startMs = new Date(inst.date_updated + "Z").getTime();
    const nowMs = Date.now();
    const totalSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const columns: PaginatedTableColumn<VastInstance>[] = [
    {
      key: "base_model",
      header: "Model",
      render: (inst) => <span className="font-gc-med">{inst.base_model}</span>,
    },
    {
      key: "gpu_type",
      header: "GPU",
      width: "w-40",
      render: (inst) =>
        inst.gpu_type ? `${inst.gpu_count}x ${inst.gpu_type}` : "—",
    },
    {
      key: "status",
      header: "Status",
      width: "w-32",
      render: (inst) => (
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
            STATUS_VARIANT[inst.status] || STATUS_VARIANT.STOPPED
          }`}
        >
          {inst.status}
        </span>
      ),
    },
    {
      key: "date_updated",
      header: "Duration",
      width: "w-28",
      cellClassName: "text-sm font-mono text-muted-foreground",
      render: formatDuration,
    },
    {
      key: "cost_per_hour",
      header: "Cost/hr",
      width: "w-24",
      cellClassName: "text-sm font-mono text-muted-foreground",
      render: (inst) =>
        inst.cost_per_hour != null
          ? `$${inst.cost_per_hour.toFixed(2)}/hr`
          : "—",
    },
    {
      key: "adapter_count",
      header: "Adapters",
      width: "w-28",
      render: (inst) => (
        <span
          className={
            inst.adapter_count >= inst.max_adapter_count
              ? "text-red-500"
              : "text-muted-foreground"
          }
        >
          {inst.adapter_count}/{inst.max_adapter_count}
        </span>
      ),
    },
    {
      key: "date_created",
      header: "Created",
      width: "w-44",
      cellClassName: "text-xs text-muted-foreground whitespace-nowrap",
      render: (inst) =>
        inst.date_created
          ? new Date(inst.date_created + "Z").toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })
          : "—",
    },
    {
      key: "id" as keyof VastInstance,
      header: "",
      width: "w-10",
      render: (inst) => {
        if (busyIds.has(inst.id)) {
          return <RefreshCwIcon className="w-4 h-4 animate-spin text-muted-foreground" />;
        }
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {inst.status === "STOPPED" && (
                  <DropdownMenuItem onClick={(e) => handleStartInstance(inst, e)}>
                    <PlayIcon className="w-4 h-4 mr-2" />
                    Start
                  </DropdownMenuItem>
                )}
                {inst.status === "RUNNING" && (
                  <DropdownMenuItem onClick={(e) => handleStopInstance(inst, e)}>
                    <SquareIcon className="w-4 h-4 mr-2" />
                    Stop
                  </DropdownMenuItem>
                )}
                {inst.status !== "DESTROYED" && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={(e) => handleDestroyInstance(inst, e)}
                  >
                    <Trash2Icon className="w-4 h-4 mr-2" />
                    Destroy
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">Instances</h2>
        <div className="flex items-center gap-2">
          <Button variant="default" size="sm" onClick={() => setWizardOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            New Instance
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchInstances(currentPage)}
            disabled={isLoading}
          >
            <RefreshCwIcon
              className={`w-4 h-4 mr-1.5 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Inference instances and loaded LoRA adapters. Refreshes every 60s.
      </p>

      <div className="flex gap-2 mb-4">
        <Badge variant="secondary">{totalCount} total</Badge>
        <Badge className="bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20">
          {stats.running} running
        </Badge>
        {stats.provisioning > 0 && (
          <Badge className="bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20">
            {stats.provisioning} provisioning
          </Badge>
        )}
        <Badge variant="secondary">{stats.total_adapters} adapters loaded</Badge>
      </div>

      <PaginatedTable
        data={instances}
        columns={columns}
        loading={isLoading}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        pageSize={PAGE_SIZE}
        totalItems={totalCount}
        serverSidePagination
        emptyTitle="No instances found"
        emptyDescription="No inference instances have been created yet."
        onRowClick={(inst: VastInstance) => navigate(`/admin/instances/${inst.id}`)}
        rowClassName={(inst: VastInstance) =>
          `cursor-pointer ${busyIds.has(inst.id) ? "opacity-50 pointer-events-none animate-pulse" : ""}`
        }
      />

      <CreateInstanceWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        apiKey={apiKey}
        onCreated={() => {
          setWizardOpen(false);
          fetchInstances(currentPage);
        }}
      />

      <AlertDialog
        open={confirm.open}
        onOpenChange={(open) => setConfirm((c) => ({ ...c, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirm.destructive
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
              onClick={() => {
                confirm.onConfirm();
                setConfirm((c) => ({ ...c, open: false }));
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VastInstancesOverview;
